import { useAuth } from "@/_core/hooks/useAuth";
import PhotoGallery from "@/components/PhotoGallery";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { trpc } from "@/lib/trpc";
import { ArrowLeft, Edit, Plus, Trash2, Lock, CheckCircle2 } from "lucide-react";
import { useState } from "react";
import { useLocation } from "wouter";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { UnlockLogModal } from "@/components/UnlockLogModal";

export default function DailyLogList() {
  const { user } = useAuth();
  const [, navigate] = useLocation();
  
  const [wellFilter, setWellFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [unlockId, setUnlockId] = useState<number | null>(null);
  const [unlockModalOpen, setUnlockModalOpen] = useState(false);

  const { data: wells } = trpc.wells.list.useQuery({ activeOnly: true });
  const { data: logs, refetch } = trpc.dailyLogs.list.useQuery({
    wellId: wellFilter && wellFilter !== "all" ? parseInt(wellFilter) : undefined,
    status: statusFilter !== "all" ? (statusFilter as any) : undefined,
    startDate: startDate ? new Date(startDate) : undefined,
    endDate: endDate ? new Date(endDate) : undefined,
  });

  const deleteLogMutation = trpc.dailyLogs.delete.useMutation();
  const unlockLogMutation = trpc.dailyLogs.unlock.useMutation();

  const handleUnlock = async (logId: number, editReason: string) => {
    try {
      await unlockLogMutation.mutateAsync({ id: logId, editReason });
      toast.success("Log unlocked successfully");
      setUnlockModalOpen(false);
      setUnlockId(null);
      refetch();
      // Navigate to edit page
      navigate(`/logs/${logId}/edit`);
    } catch (error: any) {
      toast.error(error.message || "Failed to unlock log");
    }
  };
  
  const handleEdit = (log: any) => {
    if (log.status === "draft") {
      // Draft logs can be edited directly
      navigate(`/logs/${log.id}/edit`);
    } else if (log.status === "submitted" || log.status === "locked") {
      // Submitted/locked logs require admin unlock
      if (user?.role === "admin") {
        setUnlockId(log.id);
        setUnlockModalOpen(true);
      } else {
        toast.error("Only admins can unlock submitted or locked logs");
      }
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      await deleteLogMutation.mutateAsync({ id: deleteId });
      toast.success("Log deleted successfully");
      refetch();
      setDeleteId(null);
    } catch (error: any) {
      toast.error(error.message || "Failed to delete log");
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, string> = {
      draft: "status-draft",
      submitted: "status-submitted",
      locked: "status-locked",
    };
    
    return (
      <Badge className={variants[status] || ""}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const getWellName = (wellId: number) => {
    return wells?.find(w => w.id === wellId)?.wellName || `Well #${wellId}`;
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-card sticky top-0 z-10">
        <div className="container py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate("/")}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
              <h1 className="text-xl font-bold">Daily Logs</h1>
            </div>
            <Button onClick={() => navigate("/logs/new")}>
              <Plus className="w-4 h-4 mr-2" />
              New Log
            </Button>
          </div>
        </div>
      </header>

      <main className="container py-6">
        {/* Filters */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Filters</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <Label htmlFor="well-filter">Well</Label>
                <Select value={wellFilter} onValueChange={setWellFilter}>
                  <SelectTrigger id="well-filter">
                    <SelectValue placeholder="All Wells" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Wells</SelectItem>
                    {wells?.map((well) => (
                      <SelectItem key={well.id} value={well.id.toString()}>
                        {well.wellName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="status-filter">Status</Label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger id="status-filter">
                    <SelectValue placeholder="All Statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="submitted">Submitted</SelectItem>
                    <SelectItem value="locked">Locked</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="start-date">Start Date</Label>
                <Input
                  id="start-date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>

              <div>
                <Label htmlFor="end-date">End Date</Label>
                <Input
                  id="end-date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Logs List */}
        <div className="space-y-4">
          {logs && logs.length === 0 && (
            <Card>
              <CardContent className="py-12 text-center">
                <p className="text-muted-foreground">No logs found</p>
                <Button className="mt-4" onClick={() => navigate("/logs/new")}>
                  Create Your First Log
                </Button>
              </CardContent>
            </Card>
          )}

          {logs?.map((log) => (
            <Card key={log.id}>
              <CardContent className="pt-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-lg">{getWellName(log.wellId)}</h3>
                      {getStatusBadge(log.status)}
                    </div>
                    <div className="text-sm text-muted-foreground space-y-1">
                      <p>
                        Date: {new Date(log.logDate).toLocaleDateString()} 
                        {log.logTime && ` at ${log.logTime}`}
                      </p>
                      {log.operatorInitial && <p>Operator: {log.operatorInitial}</p>}
                      {log.gallonsPumped && <p>Gallons Pumped: {log.gallonsPumped}</p>}
                      {log.remarks && (
                        <p className="line-clamp-2">Remarks: {log.remarks}</p>
                      )}
                      {(log.extraJson && typeof log.extraJson === 'object' && Object.keys(log.extraJson).length > 0) ? (
                        <details className="mt-2">
                          <summary className="cursor-pointer text-primary hover:underline">Custom Fields</summary>
                          <div className="mt-1 pl-4 space-y-1">
                            {Object.entries(log.extraJson as Record<string, any>).map(([key, value]) => (
                              <p key={key}>
                                <span className="font-medium">{key}:</span> {String(value)}
                              </p>
                            ))}
                          </div>
                        </details>
                      ) : null}
                      <details className="mt-2">
                        <summary className="cursor-pointer text-primary hover:underline">Photos</summary>
                        <div className="mt-2">
                          <PhotoGallery dailyLogId={log.id} canDelete={user?.role === 'admin' || log.createdBy === user?.id} />
                        </div>
                      </details>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/logs/${log.id}/edit`)}
                      disabled={log.status === 'locked' && user?.role !== 'admin'}
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(log)}
                      title={log.status === "draft" ? "Edit log" : "Unlock and edit log"}
                    >
                      {log.status === "draft" ? (
                        <Edit className="w-4 h-4" />
                      ) : (
                        <Lock className="w-4 h-4" />
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setDeleteId(log.id)}
                      disabled={log.createdBy !== user?.id && user?.role !== 'admin'}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteId !== null} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Log</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this log? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Unlock Log Modal */}
      <UnlockLogModal
        open={unlockModalOpen}
        onOpenChange={setUnlockModalOpen}
        logId={unlockId}
        onUnlock={handleUnlock}
        isLoading={unlockLogMutation.isPending}
      />
    </div>
  );
}
